import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  UploadCloud, Download, ClipboardList, CheckCircle2, XCircle, Loader2, ArrowLeft, FileSpreadsheet, RotateCcw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffBulkUploadPage() {
  const [tab, setTab] = useState("upload");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { batch_id, valid_rows, invalid_rows, total, valid, invalid }
  const [confirming, setConfirming] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/org/staff-import/history");
      setHistory(data.data.batches);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHistoryLoading(false); }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/org/staff-import/template`, { credentials: "include" });
      if (!res.ok) throw new Error("Template download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "staff_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "Could not download template."); }
  };

  const submitPreview = async () => {
    if (!file) return toast.error("Please select a .csv or .xlsx file first.");
    setUploading(true); setPreview(null); setConfirmResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/org/staff-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.data);
      toast.success(`Preview ready: ${data.data.valid} valid · ${data.data.invalid} invalid.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const confirmImport = async (strict = false) => {
    if (!preview) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/org/staff-import/confirm", { batch_id: preview.batch_id, strict });
      setConfirmResult(data.data);
      toast.success(data.message);
      loadHistory();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirming(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setConfirmResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <DashboardLayout title="Bulk staff upload" breadcrumbs={["My Institute", "Staff", "Bulk upload"]}>
      <div className="space-y-6" data-testid={ADMIN.staffImportRoot}>
        <div className="flex items-center justify-between">
          <Link to="/app/hospital/staff" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Staff
          </Link>
          <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid={ADMIN.staffImportTemplate}>
            <Download className="h-3.5 w-3.5" /> Download CSV template
          </button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="upload" data-testid="staff-import-tab-upload"><UploadCloud className="mr-1 h-4 w-4" /> Upload</TabsTrigger>
            <TabsTrigger value="history" data-testid="staff-import-tab-history"><ClipboardList className="mr-1 h-4 w-4" /> Import history</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-6">
            {!preview && !confirmResult && (
              <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
                <FileSpreadsheet className="mx-auto h-10 w-10 text-slate-400" />
                <h3 className="mt-3 text-lg font-bold text-slate-900">Upload staff list</h3>
                <p className="mt-1 text-sm text-slate-500">CSV or XLSX (max 4 MB). Use the template above to guarantee the right columns.</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-4 mx-auto block cursor-pointer rounded-md border border-slate-200 bg-slate-50/60 p-2 text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-[#101876] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white"
                  data-testid={ADMIN.staffImportUpload}
                />
                {file && <p className="mt-3 text-xs text-slate-500">Selected: <span className="font-semibold text-slate-700">{file.name}</span> ({Math.ceil(file.size / 1024)} KB)</p>}
                <button
                  onClick={submitPreview}
                  disabled={!file || uploading}
                  className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
                  data-testid={ADMIN.staffImportPreview}
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  Validate & preview
                </button>
              </div>
            )}

            {preview && !confirmResult && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Total rows</div>
                    <div className="mt-1 text-3xl font-black text-slate-900">{preview.total}</div>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Valid</div>
                    <div className="mt-1 text-3xl font-black text-emerald-800">{preview.valid}</div>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-rose-700">Invalid</div>
                    <div className="mt-1 text-3xl font-black text-rose-800">{preview.invalid}</div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white">
                  <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">Preview ({preview.valid + preview.invalid} rows)</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Row</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Emp. ID</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Dept</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...preview.valid_rows, ...preview.invalid_rows].map((r, i) => (
                        <TableRow key={i} className={r._error ? "bg-rose-50/40" : "bg-emerald-50/30"}>
                          <TableCell className="text-xs text-slate-500">{r._row}</TableCell>
                          <TableCell className="text-sm text-slate-800">{r.first_name} {r.last_name}</TableCell>
                          <TableCell className="text-xs text-slate-600">{r.email || "—"}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-600">{r.employee_id || "—"}</TableCell>
                          <TableCell className="text-xs text-slate-600">{r.role_code}</TableCell>
                          <TableCell className="text-xs text-slate-600">{r.department_code || "—"}</TableCell>
                          <TableCell>
                            {r._error ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700"><XCircle className="h-3.5 w-3.5" /> {r._error}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-xs text-slate-500">
                    {preview.invalid > 0
                      ? `${preview.invalid} row(s) will be skipped. Only ${preview.valid} valid staff will be created.`
                      : "All rows are valid and ready to import."}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <RotateCcw className="h-3.5 w-3.5" /> Start over
                    </button>
                    <button
                      onClick={() => confirmImport(false)}
                      disabled={confirming || preview.valid === 0}
                      className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-semibold text-white hover:bg-[#256a0d] disabled:opacity-70"
                      data-testid={ADMIN.staffImportConfirm}
                    >
                      {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Import {preview.valid} valid rows
                    </button>
                  </div>
                </div>
              </div>
            )}

            {confirmResult && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
                <h3 className="mt-3 text-lg font-bold text-emerald-900">Import complete</h3>
                <p className="mt-1 text-sm text-emerald-800">
                  <span className="font-bold">{confirmResult.created}</span> staff created · <span className="font-bold">{confirmResult.failed}</span> failed
                </p>
                {confirmResult.generated_credentials?.length > 0 && (
                  <div className="mt-4 text-left">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Generated logins ({confirmResult.generated_credentials.length}) — shown only once</div>
                      <button
                        onClick={() => {
                          const rows = ["email,login_id,full_name,employee_id,password"]
                            .concat(confirmResult.generated_credentials.map((c) => `${c.email},${c.login_id || ""},"${c.full_name}",${c.employee_id || ""},${c.password}`));
                          const blob = new Blob([rows.join("\n")], { type: "text/csv" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url; a.download = "generated_logins.csv"; a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                        data-testid="staff-import-download-creds"
                      >
                        <Download className="h-3.5 w-3.5" /> Download credentials CSV
                      </button>
                    </div>
                    <div className="max-h-64 overflow-auto rounded-md border border-emerald-200 bg-white">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500">Login ID</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500">Password</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {confirmResult.generated_credentials.map((c, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs">{c.full_name}</TableCell>
                              <TableCell className="font-mono text-xs text-emerald-900">{c.login_id || "—"}</TableCell>
                              <TableCell className="text-xs">{c.email}</TableCell>
                              <TableCell className="font-mono text-xs text-emerald-900">{c.password}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-2 text-[11px] text-emerald-700">Save this list — passwords will not be shown again.</div>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button onClick={reset} className="rounded-md border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50">Upload another</button>
                  <button onClick={() => navigate("/app/hospital/staff")} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]">Go to staff list</button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <div className="rounded-2xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">Recent imports</div>
              {historyLoading ? <LoadingBlock label="Loading history…" /> : history.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-500">No imports yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">File</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Total</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Valid</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Imported</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Failed</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="text-sm text-slate-800">{b.file_name}</TableCell>
                        <TableCell>{b.total_rows}</TableCell>
                        <TableCell>{b.valid_rows}</TableCell>
                        <TableCell className="font-semibold text-emerald-700">{b.successful_rows || 0}</TableCell>
                        <TableCell className="font-semibold text-rose-700">{b.failed_rows || 0}</TableCell>
                        <TableCell className="text-xs text-slate-600">{b.status}</TableCell>
                        <TableCell className="text-xs text-slate-500">{b.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
